/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Form Handler Module - Обработка форм
 * 
 * Phase 6: Filters and Forms Migration
 * Handles form submission, validation, and state management
 */

import { FormDOM } from './form-handler.dom.js';
import { FormValidation } from './form-handler.validation.js';

/**
 * Form Handler Class - manages form operations
 */
class FormHandler {
    constructor() {
        this.forms = new Map();
        this.validators = new Map();
        this.globalCallbacks = [];
        
        this.defaultConfig = {
            validateOnInput: true,
            validateOnSubmit: true,
            preventDefault: true,
            showErrors: true,
            clearErrorsOnInput: true,
            debounceDelay: 300
        };
        
        // Initialization logging only in debug mode
    }
    
    /**
     * Initialize form handler
     * @param {Object} options - Configuration options
     * @returns {boolean} Success status
     */
    initialize(options = {}) {
        try {
            const config = { ...this.defaultConfig, ...options };
            
            // Auto-register forms if specified
            if (config.autoRegister) {
                this.autoRegisterForms(config.formSelectors || ['form']);
            }
            
            // Setup global form event listeners
            this.setupGlobalEventListeners();
            
            if (window.HyperLog) {
                window.HyperLog.debug('[FormHandler] Initialized successfully', {
                    config: config,
                    formsCount: this.forms.size
                });
            }
            
            return true;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[FormHandler] Initialization failed:', error);
            }
            return false;
        }
    }
    
    /**
     * Register a form for handling
     * @param {string} formId - Form identifier
     * @param {Object} config - Form configuration
     * @returns {boolean} Success status
     */
    registerForm(formId, config = {}) {
        try {
            const formConfig = {
                selector: `#${formId}`,
                ...this.defaultConfig,
                ...config
            };
            
            const formElement = $(formConfig.selector);
            if (formElement.length === 0) {
                if (debugMode === '1') {
                console.warn(`[FormHandler] Form not found: ${formConfig.selector}`);
            }
                return false;
            }
            
            // Store form configuration
            this.forms.set(formId, {
                config: formConfig,
                element: formElement,
                state: {
                    isValid: true,
                    isDirty: false,
                    isSubmitting: false,
                    errors: {}
                },
                callbacks: {
                    onSubmit: [],
                    onValidate: [],
                    onChange: [],
                    onError: []
                }
            });
            
            // Setup form event listeners
            this.setupFormEventListeners(formId);
            
            if (window.HyperLog) {
                window.HyperLog.debug('[FormHandler] Form registered', {
                    formId: formId,
                    selector: formConfig.selector
                });
            }
            
            return true;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[FormHandler] Error registering form:', error);
            }
            return false;
        }
    }
    
    /**
     * Auto-register forms based on selectors
     * @param {Array} selectors - Array of form selectors
     */
    autoRegisterForms(selectors) {
        for (const selector of selectors) {
            $(selector).each((index, element) => {
                const formId = element.id || `form_${index}_${Date.now()}`;
                if (!element.id) {
                    element.id = formId;
                }
                this.registerForm(formId);
            });
        }
    }
    
    /**
     * Setup event listeners for a specific form
     * @param {string} formId - Form identifier
     */
    setupFormEventListeners(formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { element, config } = formData;
        
        // Form submission
        element.on('submit', (event) => {
            this.handleFormSubmit(event, formId);
        });
        
        // Input changes
        if (config.validateOnInput || config.clearErrorsOnInput) {
            element.on('input change', 'input, select, textarea', (event) => {
                this.handleInputChange(event, formId);
            });
        }
        
        // Form reset
        element.on('reset', (event) => {
            this.handleFormReset(event, formId);
        });
    }
    
    /**
     * Setup global event listeners
     */
    setupGlobalEventListeners() {
        // Global form submission handler for unregistered forms
        $(document).on('submit', 'form', (event) => {
            const formId = event.target.id;
            if (formId && !this.forms.has(formId)) {
                // Auto-register the form
                this.registerForm(formId);
                // Handle the submission
                this.handleFormSubmit(event, formId);
            }
        });
    }
    
    /**
     * Handle form submission
     * @param {Event} event - Submit event
     * @param {string} formId - Form identifier
     */
    async handleFormSubmit(event, formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { config, state, callbacks } = formData;
        
        // Prevent default if configured
        if (config.preventDefault) {
            event.preventDefault();
        }
        
        try {
            // Set submitting state
            state.isSubmitting = true;
            this.updateFormState(formId);
            
            // Validate form if required
            if (config.validateOnSubmit) {
                const isValid = await this.validateForm(formId);
                if (!isValid) {
                    state.isSubmitting = false;
                    this.updateFormState(formId);
                    return false;
                }
            }
            
            // Get form data
            const data = this.getFormData(formId);
            
            // Execute submit callbacks
            const results = await Promise.all(
                callbacks.onSubmit.map(callback => 
                    this.executeCallback(callback, { formId, data, event })
                )
            );
            
            // Check if any callback failed
            const hasFailure = results.some(result => result === false);
            if (hasFailure) {
                throw new Error('Form submission callback failed');
            }
            
            // Reset dirty state on successful submission
            state.isDirty = false;
            state.isSubmitting = false;
            this.updateFormState(formId);
            
            if (window.HyperLog) {
                window.HyperLog.debug('[FormHandler] Form submitted successfully', {
                    formId: formId,
                    data: data
                });
            }
            
            return true;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[FormHandler] Form submission error:', error);
            }
            
            state.isSubmitting = false;
            this.updateFormState(formId);
            
            // Execute error callbacks
            this.executeErrorCallbacks(formId, error);
            
            return false;
        }
    }
    
    /**
     * Handle input change
     * @param {Event} event - Input event
     * @param {string} formId - Form identifier
     */
    handleInputChange(event, formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { config, state, callbacks } = formData;
        
        // Mark form as dirty
        state.isDirty = true;
        
        // Clear errors for this field if configured
        if (config.clearErrorsOnInput) {
            const fieldName = event.target.name;
            if (fieldName && state.errors[fieldName]) {
                delete state.errors[fieldName];
                this.clearFieldError(formId, fieldName);
            }
        }
        
        // Validate on input if configured
        if (config.validateOnInput) {
            // Debounce validation
            clearTimeout(this.validationTimeout);
            this.validationTimeout = setTimeout(() => {
                this.validateField(formId, event.target.name);
            }, config.debounceDelay);
        }
        
        // Execute change callbacks
        const data = this.getFormData(formId);
        callbacks.onChange.forEach(callback => 
            this.executeCallback(callback, { formId, fieldName: event.target.name, data, event })
        );
        
        this.updateFormState(formId);
    }
    
    /**
     * Handle form reset
     * @param {Event} event - Reset event
     * @param {string} formId - Form identifier
     */
    handleFormReset(event, formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { state } = formData;
        
        // Reset state
        state.isDirty = false;
        state.isValid = true;
        state.errors = {};
        
        // Clear all error displays
        this.clearAllErrors(formId);
        
        this.updateFormState(formId);
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FormHandler] Form reset', { formId: formId });
        }
    }
    
    /**
     * Validate entire form
     * @param {string} formId - Form identifier
     * @returns {boolean} Validation result
     */
    async validateForm(formId) {
        const formData = this.forms.get(formId);
        if (!formData) return false;
        const { element } = formData;
        const isValid = await FormValidation.validateForm(formData, element, () => this.getFormData(formId));
        if (!isValid && formData.config.showErrors) this.displayErrors(formId);
        this.updateFormState(formId);
        return isValid;
    }
    
    /**
     * Validate specific field
     * @param {string} formId - Form identifier
     * @param {string} fieldName - Field name
     * @returns {boolean} Field validation result
     */
    async validateField(formId, fieldName) {
        const formData = this.forms.get(formId);
        if (!formData || !fieldName) return false;
        const { element, state } = formData;
        const ok = await FormValidation.validateField(formData, element, fieldName);
        if (!ok) {
            this.displayFieldError(formId, fieldName, state.errors[fieldName]);
        } else {
            this.clearFieldError(formId, fieldName);
        }
        state.isValid = Object.keys(state.errors).length === 0;
        this.updateFormState(formId);
        return ok;
    }
    
    /**
     * Get form data as object
     * @param {string} formId - Form identifier
     * @returns {Object} Form data
     */
    getFormData(formId) {
        const formData = this.forms.get(formId);
        if (!formData) return {};
        
        const { element } = formData;
        const data = {};
        
        // Get all form inputs
        element.find('input, select, textarea').each((index, field) => {
            const name = field.name;
            if (!name) return;
            
            if (field.type === 'checkbox') {
                if (data[name]) {
                    // Multiple checkboxes with same name
                    if (!Array.isArray(data[name])) {
                        data[name] = [data[name]];
                    }
                    if (field.checked) {
                        data[name].push(field.value);
                    }
                } else {
                    data[name] = field.checked ? field.value : '';
                }
            } else if (field.type === 'radio') {
                if (field.checked) {
                    data[name] = field.value;
                }
            } else {
                data[name] = field.value;
            }
        });
        
        return data;
    }
    
    /**
     * Set form data
     * @param {string} formId - Form identifier
     * @param {Object} data - Data to set
     */
    setFormData(formId, data) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { element } = formData;
        
        for (const [name, value] of Object.entries(data)) {
            const field = element.find(`[name="${name}"]`);
            
            if (field.length > 0) {
                if (field.attr('type') === 'checkbox') {
                    field.prop('checked', !!value);
                } else if (field.attr('type') === 'radio') {
                    field.filter(`[value="${value}"]`).prop('checked', true);
                } else {
                    field.val(value);
                }
            }
        }
        
        // Mark as dirty and validate
        formData.state.isDirty = true;
        this.updateFormState(formId);
    }
    
    /**
     * Add callback for form events
     * @param {string} formId - Form identifier
     * @param {string} event - Event type ('submit\', 'validate', \'change', 'error')
     * @param {Function} callback - Callback function
     */
    addCallback(formId, event, callback) {
        const formData = this.forms.get(formId);
        if (!formData || typeof callback !== 'function') return;
        
        if (formData.callbacks[event]) {
            formData.callbacks[event].push(callback);
        }
    }
    
    /**
     * Execute callback safely
     * @param {Function} callback - Callback to execute
     * @param {Object} params - Parameters to pass
     * @returns {*} Callback result
     */
    async executeCallback(callback, params) {
        try {
            return await callback(params);
        } catch (error) {
            if (debugMode === '1') {
                console.error('[FormHandler] Callback execution error:', error);
            }
            return false;
        }
    }
    
    /**
     * Execute error callbacks
     * @param {string} formId - Form identifier
     * @param {Error} error - Error object
     */
    executeErrorCallbacks(formId, error) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        formData.callbacks.onError.forEach(callback => 
            this.executeCallback(callback, { formId, error })
        );
    }
    
    /**
     * Display form errors
     * @param {string} formId - Form identifier
     */
    displayErrors(formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { state } = formData;
        
        for (const [fieldName, error] of Object.entries(state.errors)) {
            this.displayFieldError(formId, fieldName, error);
        }
    }
    
    /**
     * Display field error
     * @param {string} formId - Form identifier
     * @param {string} fieldName - Field name
     * @param {string} error - Error message
     */
    displayFieldError(formId, fieldName, error) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { element } = formData;
        FormDOM.displayFieldError(element, fieldName, error);
    }
    
    /**
     * Clear field error
     * @param {string} formId - Form identifier
     * @param {string} fieldName - Field name
     */
    clearFieldError(formId, fieldName) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { element } = formData;
        FormDOM.clearFieldError(element, fieldName);
    }
    
    /**
     * Clear all form errors
     * @param {string} formId - Form identifier
     */
    clearAllErrors(formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { element } = formData;
        FormDOM.clearAllErrors(element);
    }
    
    /**
     * Update form state and trigger state callbacks
     * @param {string} formId - Form identifier
     */
    updateFormState(formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { element, state } = formData;
        
        // Update form classes based on state
        FormDOM.updateFormStateClasses(element, state);
        
        // Trigger global callbacks
        this.globalCallbacks.forEach(callback => 
            this.executeCallback(callback, { formId, state })
        );
    }
    
    /**
     * Get form state
     * @param {string} formId - Form identifier
     * @returns {Object} Form state
     */
    getFormState(formId) {
        const formData = this.forms.get(formId);
        return formData ? { ...formData.state } : null;
    }
    
    /**
     * Add global callback
     * @param {Function} callback - Global callback function
     */
    addGlobalCallback(callback) {
        if (typeof callback === 'function') {
            this.globalCallbacks.push(callback);
        }
    }
    
    /**
     * Remove form from handler
     * @param {string} formId - Form identifier
     */
    unregisterForm(formId) {
        const formData = this.forms.get(formId);
        if (!formData) return;
        
        const { element } = formData;
        
        // Remove event listeners
        element.off('submit input change reset');
        
        // Remove form from map
        this.forms.delete(formId);
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FormHandler] Form unregistered', { formId: formId });
        }
    }
    
    /**
     * Get statistics about form handler
     * @returns {Object} Statistics object
     */
    getStats() {
        return {
            formsCount: this.forms.size,
            registeredForms: Array.from(this.forms.keys()),
            globalCallbacksCount: this.globalCallbacks.length
        };
    }
    
    /**
     * Destroy form handler
     */
    destroy() {
        try {
            // Unregister all forms
            for (const formId of this.forms.keys()) {
                this.unregisterForm(formId);
            }
            
            // Clear global event listeners
            $(document).off('submit', 'form');
            
            // Clear timeouts
            if (this.validationTimeout) {
                clearTimeout(this.validationTimeout);
            }
            
            // Clear state
            this.forms.clear();
            this.validators.clear();
            this.globalCallbacks = [];
            
            if (window.HyperLog) {
                window.HyperLog.debug('[FormHandler] Destroyed');
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[FormHandler] Error destroying form handler:', error);
            }
        }
    }
}

// Create singleton instance
const formHandler = new FormHandler();

// Export for use in other modules
export { formHandler };

// Global export for backward compatibility
window.formHandler = formHandler;

// Register with migration controller

// Module registration logging only in debug mode 